import { type FC, Fragment, useEffect, useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Clamp, LazyImage, Loader, PrimaryButton, Slider } from '@crehana/ui';
import { customLocalStorage } from '@crehana/utils';
import { useUITheme } from '@crehana/web';
import { useRouter } from 'next/router';

import PromoVideo from './components/PromoVideo';
import { AMPLITUDE_DIALOG_NEWS } from './constants';
import useAdminDashboardUpdates from './hooks/useAdminDashboardUpdates';
import {
  ControlSlider,
  DialogBox,
  DialogContentBox,
  DialogContentInfoBox,
  DialogImgBox,
} from './NewsDialog.styles';
import type { INewsDialogItem } from './types';

const UPDATE_POPUP_SHOWN = 'updates-admindashboard-popup-shown';

type TNewsDialogPoprs = {
  slug: string;
};

const NewsDialog: FC<TNewsDialogPoprs> = ({ slug }) => {
  const router = useRouter();
  const { logEvent } = useAmplitude();
  const [isOpen, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const { loading, updates } = useAdminDashboardUpdates({ skip: !isOpen });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (!customLocalStorage.getItem(UPDATE_POPUP_SHOWN)) {
      setOpen(true);
    }
  }, [isOpen]);

  const onDismiss = () => {
    customLocalStorage.setItem(UPDATE_POPUP_SHOWN, 'true');
    setOpen(false);
    logEvent({
      event: AMPLITUDE_DIALOG_NEWS.CLOSE,
      data: {
        ...getSourceEventParams(),
        Type: '',
      },
    });
  };

  const onDetail = (update: INewsDialogItem) => {
    const redirect = `/org/${slug}/${update.url}`.replace(`//`, '/');
    if (update.type === 'course') {
      logEvent({
        event: AMPLITUDE_DIALOG_NEWS.SEE_MORE,
        data: {
          ...getSourceEventParams(),
          Type: update.type,
        },
        callback: () => window.open(update.url || '', '_blank'),
      });
    } else {
      customLocalStorage.setItem(UPDATE_POPUP_SHOWN, 'true');
      setOpen(false);
      logEvent({
        event: AMPLITUDE_DIALOG_NEWS.SEE_MORE,
        data: {
          ...getSourceEventParams(),
          Type: update.type,
        },
        callback: () => router.push(redirect),
      });
    }
  };

  return (
    <DialogBox
      size="m"
      open={isOpen && updates.length > 0}
      onDismiss={onDismiss}
      isDark={isDark}
      ignoreBackdropClick
    >
      <DialogContentBox
        isDark={isDark}
        type="m"
        className="pt-36 pb-36 sm:pb-36"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <Fragment>
            <Slider
              itemsPerPage={{ xs: 1, sm: 1, md: 1, lg: 1 }}
              desktopPaginationSelector=".pagination"
              desktopPaginationType="default-s"
            >
              {updates.map(update => (
                <div key={update.originalId || 0} className="w-full">
                  <div className="mb-8 mt-2 pt-1 sm:pl-36 sm:pr-36">
                    {update.type === 'course' ? (
                      <>
                        <DialogImgBox>
                          <PromoVideo
                            imageSource={update.image}
                            promoVideoUrl={update.promoVideoUrl}
                          />
                        </DialogImgBox>
                        <div className="w-full flex items-center justify-center mt-32 ">
                          {/* biome-ignore lint/a11y/noLabelWithoutControl: label association handled differently */}
                          <label
                            className={`font-label text-primary-main ${
                              isDark && 'text-secondary-main'
                            }`}
                          >
                            {t('NEWS_DIALOG_COURSE_TYPE')}
                          </label>
                        </div>
                      </>
                    ) : (
                      <>
                        <LazyImage
                          src={update.image || ''}
                          alt={update.title || ''}
                          style={{ height: 256 }}
                        />
                        <div className="w-full flex items-center justify-center mt-32 ">
                          {/* biome-ignore lint/a11y/noLabelWithoutControl: label association handled differently */}
                          <label
                            className={`font-label text-primary-main ${
                              isDark && 'text-secondary-main'
                            }`}
                          >
                            {t('NEWS_DIALOG_TYPE')}
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                  <DialogContentInfoBox>
                    <div className="flex justify-center w-full mb-8 sm:pl-36 sm:pr-36 md:pl-36 md:pr-36 lg:pl-36 lg:pr-36">
                      <Clamp
                        clamp={2}
                        className="w-full flex font-h4 text-center sm:pl-48 sm:pr-48 md:pl-48 md:pr-48 lg:pl-48 lg:pr-48"
                      >
                        {update.title}
                      </Clamp>
                    </div>
                    <p className="font-body3 text-center mb-16 w-full sm:pl-36 sm:pr-36 md:pl-36 md:pr-36 lg:pl-36 lg:pr-36">
                      <Clamp clamp={3}>{update.description}</Clamp>
                    </p>
                    <div className="flex justify-center w-full">
                      {updates.length > 0 && update.url && (
                        <PrimaryButton
                          size="s"
                          onClick={() => onDetail(update)}
                          label={t('NEWS_DIALOG_BTN_DETAIL')}
                          className={`mb-32 ${isDark && 'bg-primary-light'} `}
                        />
                      )}
                    </div>
                  </DialogContentInfoBox>
                </div>
              ))}
            </Slider>
            <ControlSlider
              isDark={isDark}
              className="pagination flex-center-child"
            />
          </Fragment>
        )}
      </DialogContentBox>
    </DialogBox>
  );
};

export default NewsDialog;
